import React, { useState, useEffect, useRef } from 'react';
import { MultiInput, MultiInputType, ApiError, ProviderMapper } from '../Components/Combined';
import { BackButton, Input, Select, Checkbox, LengthOfStay, ErrorAlert, Button, ErrorMessage } from '../Components/Simple';
import useAsyncPricerApi from '../Hooks/useAsyncPricerApi';
import { genderValues, poaValues, serviceModeValues } from '../Constants/dropdownValues';
import { API_DATE_STRING_FORMAT, DATE_STRING_FORMAT } from '../Constants/stringFormats';
import { PSYCH as modelType } from '../Constants/modelTypes';
import useFiscalYear from '../Hooks/useFiscalYear';
import useDrgGrouperApi from '../Hooks/useDrgGrouperApi';
import useSessionState from '../Hooks/useSessionState';
import { useNavigate, useLocation } from 'react-router-dom';
import useCollection from '../Hooks/useCollection';
import useExportRequest from '../Hooks/useExportRequest';
import useImportRequest from '../Hooks/useImportRequest';
import { formatDateStringIfMatches, getDefaultDateString } from '../Utils/stringUtils';
import FilePickerButton from '../Components/Simple/FilePickerButton';
import { mapToSetters } from '../Utils/mapping';
import { enableAsyncPricingApi } from '../Utils/features';
import usePricerApi from '../Hooks/usePricerApi';
import { EXECUTE } from '../Constants/serviceMode';

export default function IPPysch() {
    const location = useLocation();
    const { prePopulatedMedicareId, prePopulatedNPI } = location.state || {};
    const [claimId, setClaimId] = useSessionState('', 'claimId');
    const [providerName, setProviderName] = useSessionState('', 'providerName');
    const [medicareId, setMedicareId] = useSessionState(prePopulatedMedicareId ?? '', 'medicareId');
    const [npi, setNpi] = useSessionState(prePopulatedNPI ?? '', 'npi');
    const [drgCode, setDRGCode] = useSessionState('', 'drg');
    const [drgGrouperDesc, setDRGGrouperDesc] = useSessionState('', 'description');
    const [drgGrouperVersion, setDRGGrouperVersion] = useSessionState('', 'grouperVersion');
    const [drgCodeAutofilled, setDRGCodeAutofilled] = useState(false);
    const [drgSuccess, setDRGSuccess] = useState(false);
    const [dischargeStatus, setDischargeStatus] = useSessionState('', 'dischargeStatus');
    const [birthDate, setBirthDate] = useSessionState('', 'birthDate');
    const [gender, setGender] = useSessionState('', 'gender');
    const [admitDate, setAdmitDate] = useSessionState(getDefaultDateString(), 'admitDate');
    const [dischargeDate, setDischargeDate] = useSessionState(getDefaultDateString(), 'dischargeDate');
    const [fiscalYear, setFiscalYearInputDate] = useFiscalYear(null);
    const [billedAmountTotal, setBilledAmountTotal] = useSessionState('', 'billedAmountTotal');
    const [nonCoveredDays, setNonCoveredDays] = useSessionState(0, 'nonCoveredDays');
    const [previousDays, setPreviousDays] = useSessionState(0, 'previousDays');
    const [sourceOfAdmission, setSourceOfAdmission] = useSessionState(false, 'sourceOfAdmission');
    const [units, setUnits] = useSessionState('', 'units');
    const [drgServiceMode, setDrgServiceMode] = useSessionState(EXECUTE, 'drgServiceMode');

    const navigate = useNavigate();

    const getNewProcedureCode = () => {
        return { procedureCode: '' };
    };

    const getNewDisanosisCode = () => {
        return {
            diagnosisCode: '',
            poa: ''
        };
    };

    const DEFAULT_PROCEDURE_CODES = [getNewProcedureCode(), getNewProcedureCode(), getNewProcedureCode(), getNewProcedureCode(), getNewProcedureCode()];
    const DEFAULT_DIAGNOSIS_CODES = [getNewDisanosisCode(), getNewDisanosisCode(), getNewDisanosisCode(), getNewDisanosisCode(), getNewDisanosisCode()];

    const proceduresCollection = useCollection(DEFAULT_PROCEDURE_CODES, getNewProcedureCode, 'procedureCodes');
    const diagnosisesCollection = useCollection(DEFAULT_DIAGNOSIS_CODES, getNewDisanosisCode, 'diagnosisCodes');

    const pricer = enableAsyncPricingApi ? useAsyncPricerApi(modelType) : usePricerApi(modelType);
    const warmupPricer = useAsyncPricerApi(modelType, true);

    const drg = useDrgGrouperApi();

    const [fileExporting, _fileExportSuccess, fileExportError, fileExportExecute, fileExportReset] = useExportRequest(modelType, {
        "claimId": claimId,
        "providerName": providerName,
        "medicareId": medicareId,
        "npi": npi,
        "drgCode": drgCode,
        "dischargeStatus": dischargeStatus,
        "birthDate": birthDate,
        "gender": gender,
        "admitDate": admitDate,
        "dischargeDate": dischargeDate,
        "billedAmountTotal": billedAmountTotal,
        "nonCoveredDays": nonCoveredDays,
        "previousDays": previousDays,
        "sourceOfAdmission": sourceOfAdmission,
        "units": units,
        "diagnosisCodes": diagnosisesCollection.values,
        "procedureCodes": proceduresCollection.values,
        drgServiceMode
    });

    const handleFileSave = () => {
        resetApis();
        fileExportExecute();
    };

    const [fileImporting, fileImportSuccess, fileImportError, fileImportResult, fileImportReset, fileImportOnChange] = useImportRequest(modelType);

    const handleFileImportOnChange = (e) => {
        resetApis();
        fileImportOnChange(e);
    };

    const handleSubmit = async () => {
        drg.reset(); // don't want to be showing error messages
        fileExportReset();
        fileImportReset();

        if (enableAsyncPricingApi) {
            await pricer.execute(formatRequest());
        } else {
            await pricer.execute(fiscalYear, formatRequest());
        }
    };

    const formatRequest = () => {
        return {
            claimID: claimId || null,
            medicareID: medicareId || null,
            paymentMethodology: "FFS",
            dob: formatDateStringIfMatches(birthDate, DATE_STRING_FORMAT, API_DATE_STRING_FORMAT),
            gender: gender || null,
            fromDate: formatDateStringIfMatches(admitDate, DATE_STRING_FORMAT, API_DATE_STRING_FORMAT),
            toDate: formatDateStringIfMatches(dischargeDate, DATE_STRING_FORMAT, API_DATE_STRING_FORMAT),
            dischargeStatus: dischargeStatus || null,
            drg: drgCode || null,
            nonCoveredDays: nonCoveredDays || 0,
            previousDays: previousDays || 0,
            admitSource: sourceOfAdmission ? 'Y' : 'N',
            drgServiceMode,
            diagnosis: [
                ...diagnosisesCollection.values.filter(dc => dc.diagnosisCode).map(dc => {
                    return {
                        icdDiag: dc.diagnosisCode.toUpperCase() || null,
                        poa: dc.poa || null
                    }
                })
            ],
            icdProc: [
                ...proceduresCollection.values.filter(pc => pc.procedureCode).map(pc => pc.procedureCode.toUpperCase())
            ],
            serviceLineItems: [
                {
                    lineId: 1,
                    revCode: '0901',
                    units: units || null,
                    billed: isNaN(billedAmountTotal) ? 0 : Number(billedAmountTotal)
                }
            ]
        };
    }

    const handleReset = () => {
        setClaimId('');
        setProviderName('');
        setMedicareId('');
        setNpi('');
        setDRGCode('');
        setDRGGrouperDesc('');
        setDRGGrouperVersion('');
        setDRGSuccess('');
        setDischargeStatus('');
        setBirthDate('');
        setGender('');
        setAdmitDate(getDefaultDateString());
        setDischargeDate(getDefaultDateString());
        setBilledAmountTotal('');
        setNonCoveredDays('');
        setPreviousDays('');
        setSourceOfAdmission(false);
        setUnits('');
        proceduresCollection.setValues(DEFAULT_PROCEDURE_CODES);
        diagnosisesCollection.setValues(DEFAULT_DIAGNOSIS_CODES);
        resetApis();
        setDrgServiceMode(EXECUTE);
        fileExportReset();
        fileImportReset();
    };

    const resetApis = () => {
        pricer.reset();
        drg.reset();
    };

    useEffect(() => {
        if (fileImportSuccess) {
            const setters = {
                "claimId": setClaimId,
                "providerName": setProviderName,
                "medicareId": setMedicareId,
                "npi": setNpi,
                "drgCode": setDRGCode,
                "dischargeStatus": setDischargeStatus,
                "birthDate": setBirthDate,
                "gender": setGender,
                "admitDate": setAdmitDate,
                "dischargeDate": setDischargeDate,
                "billedAmountTotal": setBilledAmountTotal,
                "nonCoveredDays": setNonCoveredDays,
                "previousDays": setPreviousDays,
                "sourceOfAdmission": setSourceOfAdmission,
                "units": setUnits,
                "diagnosisCodes": diagnosisesCollection.setValues,
                "procedureCodes": proceduresCollection.setValues,
                drgServiceMode: setDrgServiceMode
            };
            handleReset();
            mapToSetters(fileImportResult, setters);
        }
    }, [fileImportSuccess]);

    useEffect(() => {
        if (drg.success && drg.result) {
            setDRGCode((drg.result.finalDRG && drg.result.finalDRG.code && drg.result.finalDRG.code.toString()) || '');
            setDRGGrouperDesc((drg.result.finalDRG.description));
            setDRGGrouperVersion((drg.result.grouperVersion));
            setDRGSuccess((drg.success));
            setDRGCodeAutofilled(true);
            drg.reset();
            setTimeout(() => {
                setDRGCodeAutofilled(false);
            }, 3000);
        }
    }, [drg.success]);

    const handleRunDrgGrouper = () => {
        pricer.reset();
        fileExportReset();
        fileImportReset();
        drg.execute({
            birthDate,
            gender,
            admitDate,
            dischargeDate,
            dischargeStatus,
            diagnosisCodes: diagnosisesCollection.values,
            procedureCodes: proceduresCollection.values
        });
    };

    useEffect(() => {
        setFiscalYearInputDate(dischargeDate);
    }, [dischargeDate]);

    useEffect(() => {
        if (pricer.success) {
            navigate('result', {
                state: {
                    result: pricer.result,
                    fiscalYear,
                    drgServiceMode,
                    dateOfBirth: birthDate
                }
            });
        }
    }, [pricer.success]);

    return (
        <div className="py-2">
            <div className="row my-2 justify-content-between sticky top g-0 border-bottom align-items-center">
                <div className="col-auto">
                    <h3>IP Psych Pricer</h3>
                    <div className="fs-6">
                        Enter all claim information requested below. Please press SUBMIT when complete to calculate the prospective payment amount.
                    </div>
                </div>
                <div className="col-auto">
                    <BackButton />
                </div>
            </div>
            {(pricer.error || drg.error || fileImportError || fileExportError) && (<div>
                <ApiError error={pricer.error} />
                <ApiError error={drg.error} />
                {fileImportError ? <ErrorMessage errors={[{ message: fileImportError }]} /> : null}
                {fileExportError ? <ErrorMessage errors={[{ message: fileExportError }]} /> : null}
            </div>)}
            {(drgSuccess) && (<div className="alert alert-success" role="alert">
                DRG Grouper Message: {drgGrouperDesc ?? null} <br />
                DRG Grouper Version Applied: {drgGrouperVersion ?? null}
            </div>)}
            <form noValidate={true}>
                <div className="row my-2">
                    <div className="col-md-3">
                        <Input id="claim-id" labelClassName="fw-bold" label="Claim ID" onChange={setClaimId} value={claimId} />
                    </div>
                    <ProviderMapper inputs={[
                        {
                            name: "providerName",
                            label: "Provider Name",
                            superscript: "1",
                            value: providerName,
                            onChange: setProviderName
                        },
                        {
                            name: "medicareId",
                            label: "Medicare ID",
                            superscript: "1",
                            value: medicareId,
                            onChange: setMedicareId
                        },
                        {
                            name: "npi",
                            label: "NPI",
                            sublabel: "(facilities may have multiple NPIs)",
                            superscript: "1",
                            value: npi,
                            onChange: setNpi
                        }
                    ]} model={modelType} />
                </div>
                <div className="row my-2">
                    <div className="col-md-3">
                        <Input label="MS-DRG" id="ms-drg" labelClassName="fw-bold" onChange={setDRGCode} value={drgCode} placeholder="000" maxLength={3} className={drgCodeAutofilled ? 'autofilled' : ''} />
                    </div>
                    {enableAsyncPricingApi && <div className="col-md-3">
                        <Select id="drg-service-mode" value={drgServiceMode} labelClassName="fw-bold" onChange={setDrgServiceMode} label="Set DRG Grouper Execution">
                            {serviceModeValues.map((v, i) => <option key={i} value={v.value}>{v.text}</option>)}
                        </Select>
                    </div>}
                    <div className="col-md-3">
                        <Input label="Discharge Status" labelClassName="fw-bold" id="discharge-status" onChange={setDischargeStatus} value={dischargeStatus} placeholder="00" maxLength={2} />
                    </div>
                    <div className="col-md-3">
                        <Input label="Date of Birth" preservePlaceholder={true} labelClassName="fw-bold" id="date-of-birth" type="date" onChange={setBirthDate} value={birthDate} placeholder={DATE_STRING_FORMAT.toUpperCase()} />
                    </div>
                    <div className="col-md-3">
                        <Select label="Gender" id="gender" labelClassName="fw-bold" onChange={setGender} value={gender}>
                            {genderValues.map(gv => <option key={gv.value} value={gv.value}>{gv.text}</option>)}
                        </Select>
                    </div>
                </div>
                <div className="row my-2">
                    <div className="col-md-3">
                        <Input label="Admit Date" preservePlaceholder={true} id="admit-date" labelClassName="fw-bold" type="date" onChange={setAdmitDate} value={admitDate} placeholder={DATE_STRING_FORMAT.toUpperCase()} />
                    </div>
                    <div className="col-md-3">
                        <Input label="Discharge Date" preservePlaceholder={true} id="discharge-date" labelClassName="fw-bold" type="date" onChange={setDischargeDate} value={dischargeDate} placeholder={DATE_STRING_FORMAT.toUpperCase()} />
                    </div>
                    <div className="col-md-3 align-self-center mt-4">
                        <LengthOfStay fromDate={admitDate} toDate={dischargeDate} nonCoveredDays={nonCoveredDays} />
                    </div>
                    <div className="col-md-3 align-self-center mt-4 fw-bold">
                        {fiscalYear !== null && <>FY{fiscalYear}</>}
                    </div>
                </div>
                <div className="row my-2">
                    <div className="col-md-3">
                        <Input label="Billed Amount - Total" labelClassName="fw-bold" id="billed-amount-total" onChange={setBilledAmountTotal} value={billedAmountTotal} placeholder="0000.00" />
                    </div>
                    <div className="col-md-3">
                        <Input label="Non-Covered Days" labelClassName="fw-bold" id="non-covered-days" type="number" onChange={setNonCoveredDays} value={nonCoveredDays} />
                    </div>
                    <div className="col-md-3">
                        <Input label="Previous Days" labelClassName="fw-bold" id="previous-days" type="number" onChange={setPreviousDays} value={previousDays} />
                    </div>
                    <div className="col-md-3 mt-4">
                        <Checkbox label="Source of Admission 'D'" labelClassName="fw-bold" id="source-of-admission" value={sourceOfAdmission} onChange={setSourceOfAdmission} />
                    </div>
                </div>
                <div className="row my-2">
                    <div className="col-md-3">
                        <Input label="Units on 0901 Revenue Codes" labelClassName="fw-bold" superscript="2" id="revenue-codes" value={units} onChange={setUnits} />
                    </div>
                </div>
                <div> <sup> 1 </sup> Pricing depends only on Medicare ID </div>
                <div> <sup> 2 </sup> ECT reimbursement also requires an ECT ICD-10 procedure code. </div>
                <hr />
                <div className="row g-0">
                    <div className="col-md-8 my-2">
                        <MultiInput label="ICD-10 Diagnosis Codes"
                            note="Enter ICD codes without the decimal."
                            subLabel="Diagnosis"
                            collection={diagnosisesCollection}
                            lineCountLabel="Total Diagnosis Codes"
                            max={30}
                            id="diagnosis-codes">
                            <MultiInputType label="Diagnosis Code"
                                name="diagnosisCode"
                                type="text"
                                maxLength={7} />
                            <MultiInputType label="POA"
                                name="poa"
                                type="select"
                                options={poaValues} />
                        </MultiInput>
                    </div>
                    <div className="col-md-4 my-2">
                        <MultiInput label="ICD-10 Procedure Codes"
                            note="Enter ICD codes without the decimal."
                            subLabel="Procedure"
                            collection={proceduresCollection}
                            lineCountLabel="Total Procedure Codes"
                            max={30}
                            id="procedure-codes">
                            <MultiInputType label="Procedure Code"
                                name="procedureCode"
                                type="text"
                                maxLength={7} />
                        </MultiInput>
                    </div>
                </div>
                <div className="row justify-content-between my-2 sticky bottom g-0 border-top">
                    <div className="col-auto align-self-center">
                        <div className="row g-0">
                            <div className="col-auto ms-2">
                                <Button className="btn-outline-secondary" id="save-request"
                                    disabled={pricer.loading || drg.loading || fileImporting || fileExporting}
                                    onClick={handleFileSave}
                                    text="Save" />
                            </div>
                            <div className="col-auto ms-2">
                                <FilePickerButton className="btn-outline-secondary" id="load-request"
                                    disabled={pricer.loading || drg.loading || fileImporting || fileExporting}
                                    onChange={handleFileImportOnChange}
                                    loading={fileImporting}
                                    text="Load"
                                    accepts=".mrpreq" />
                            </div>
                        </div>
                    </div>
                    <div className="col-auto align-self-center">
                        <ErrorAlert visible={drg.error || pricer.error || fileImportError || fileExportError} />
                    </div>
                    <div className="col-auto align-self-center">
                        <div className="row g-0 justify-cotent-end">
                            <div className="col-auto">
                                <Button className="btn-outline-secondary" id="reset-button"
                                    onClick={handleReset}
                                    disabled={pricer.loading || drg.loading || fileImporting || fileExporting}
                                    text="Reset" />
                            </div>
                            {!enableAsyncPricingApi && (<div className="col-auto ms-2">
                                <Button className="btn-outline-secondary" id="run-drg-grouper-button"
                                    onClick={handleRunDrgGrouper}
                                    disabled={pricer.loading || drg.loading || fileImporting || fileExporting}
                                    loading={drg.loading}
                                    text="Run DRG Grouper" />
                            </div>)}
                            <div className="col-auto ms-2">
                                <Button className="btn-primary" id="submit-button"
                                    disabled={pricer.loading || fileImporting || fileExporting}
                                    onClick={handleSubmit}
                                    text="Submit"
                                    loading={pricer.loading} />
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
};
